export default function QuoteBuilderModule() {
  return (
    <section id="quote-builder" className="py-24 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Content */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center bg-teal-50 rounded-lg">
                <i className="ri-file-paper-2-line text-teal-500" />
              </div>
              <span className="text-xs font-semibold uppercase tracking-widest text-teal-600">Module 05</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold text-gray-900 leading-tight tracking-tight mb-4">
              Quote PDF — Sent,<br />
              <span className="bg-gradient-to-r from-teal-500 to-emerald-500 bg-clip-text text-transparent">Accepted in One Tap</span>
            </h2>
            <p className="text-gray-500 text-base leading-relaxed mb-6">
              Convert any itinerary into a branded, professional PDF quote in seconds. Send it directly to the customer over WhatsApp or email — with an acceptance button included. No back-and-forth, no printing, no PDFs sent to spam.
            </p>
            <ul className="flex flex-col gap-4">
              {[
                { icon: 'ri-file-pdf-line', title: 'Branded PDF generation', desc: 'Your agency logo, colors, and contact details. The PDF looks like it was designed by a professional — automatically.' },
                { icon: 'ri-send-plane-2-line', title: 'Send via WhatsApp or email', desc: 'Choose your channel. The quote is delivered with a personalised message and the customer\'s name pre-filled.' },
                { icon: 'ri-cursor-line', title: 'Customer acceptance button', desc: 'The quote includes a clear "Accept This Quote" button. Customer taps it — you get an instant notification.' },
                { icon: 'ri-eye-line', title: 'Read & open tracking', desc: 'Know exactly when the customer opened the quote, so your agent can follow up at the perfect moment.' },
              ].map((item) => (
                <li key={item.title} className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-50 rounded-lg shrink-0 mt-0.5">
                    <i className={`${item.icon} text-teal-600 text-sm`} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800 mb-0.5">{item.title}</p>
                    <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: Quote PDF mockup */}
          <div className="bg-gray-50 rounded-2xl border border-gray-100 p-5">
            {/* Simulated PDF preview */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* PDF header */}
              <div className="bg-gradient-to-r from-teal-600 to-emerald-600 px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-white/20 rounded-lg flex items-center justify-center">
                    <i className="ri-plane-line text-white text-sm" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">Sun & Sands Travel Co.</p>
                    <p className="text-teal-200 text-[10px]">Travel Proposal — Ref #QT-2024-0089</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-white text-xs font-semibold">Quote Date</p>
                  <p className="text-teal-200 text-[10px]">March 30, 2026</p>
                </div>
              </div>

              {/* Customer info */}
              <div className="px-5 py-3 border-b border-gray-100 flex justify-between items-center">
                <div>
                  <p className="text-xs text-gray-400">Prepared for</p>
                  <p className="text-sm font-semibold text-gray-800">Sara Al-Farsi</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400">Validity</p>
                  <p className="text-xs font-medium text-gray-700">Valid until Apr 5, 2026</p>
                </div>
              </div>

              {/* Line items */}
              <div className="px-5 py-3 space-y-2">
                {[
                  { item: 'Return Flights (EK) — 2 pax', amount: '$980' },
                  { item: '7N Ubud Luxury Villa', amount: '$980' },
                  { item: 'Private Airport Transfers', amount: '$120' },
                  { item: 'Activities & Tours Package', amount: '$420' },
                  { item: 'Travel Insurance', amount: '$90' },
                ].map((row) => (
                  <div key={row.item} className="flex items-center justify-between">
                    <p className="text-xs text-gray-600">{row.item}</p>
                    <p className="text-xs font-semibold text-gray-800">{row.amount}</p>
                  </div>
                ))}
                <div className="pt-2 mt-1 border-t border-gray-100 flex items-center justify-between">
                  <p className="text-sm font-bold text-gray-900">Total Package Price</p>
                  <p className="text-lg font-bold text-teal-600">$2,590</p>
                </div>
              </div>

              {/* Acceptance CTA */}
              <div className="px-5 py-4 bg-teal-50 border-t border-teal-100">
                <p className="text-xs text-gray-500 mb-2 text-center">Ready to confirm your Bali adventure?</p>
                <button type="button" className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-xs font-bold py-2.5 rounded-lg cursor-pointer whitespace-nowrap hover:from-teal-600 hover:to-emerald-600 transition-all">
                  ✓ Accept This Quote
                </button>
                <div className="flex gap-4 mt-3 justify-center">
                  <button type="button" className="flex items-center gap-1 text-[10px] text-gray-500 cursor-pointer hover:text-teal-600 transition-colors">
                    <i className="ri-whatsapp-line text-teal-500" /> Send via WhatsApp
                  </button>
                  <button type="button" className="flex items-center gap-1 text-[10px] text-gray-500 cursor-pointer hover:text-teal-600 transition-colors">
                    <i className="ri-mail-line text-teal-500" /> Send via Email
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
